import { ApolloError, NetworkStatus } from '@apollo/client';

import {
  ESponsorDirectoryAccessConfig,
  MembershipStatus,
  EUserRole,
} from '@/graphql-types/globalTypes';
import { MEMBERSHIP_STATUS } from '@/utilities';

import { useCurrent } from './useCurrent';

export const useAnalyticsUserProperties = (): {
  properties: {
    currentUserId: string | null;
    associationCode: string | undefined;
    vendorDirectory: boolean | undefined;
    communityAccess: boolean | undefined;
    userType: string | undefined;
    displayName: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
    email: string | undefined;
    tier: string | undefined;
    role: string | undefined;
    membershipStatus: string | undefined;
    integrationId: string | undefined;
    integrationIsMember: boolean;
    platformIsMember: boolean;
    isMember: boolean;
  };
} => {
  const current = useCurrent();
  const currentUserId = current.user?._id || null;
  const associationCode = current.tenant?.associationCode;
  const vendorDirectory = !!current.tenant?.sponsorDirectoryAccessConfig
    ? current.tenant.sponsorDirectoryAccessConfig !==
      ESponsorDirectoryAccessConfig.HIDDEN
    : undefined;
  // doesn't count as community access if the user is an organization account owner or organization page admin
  const communityAccess =
    current.user?.role === EUserRole.ORGANIZATION_ACCOUNT_OWNER ||
    current.user?.role === EUserRole.ORGANIZATION_PAGE_ADMIN
      ? false
      : current.user?.canAccessCommunity;
  const userType = current.user?.analyticsUserType;
  const displayName = current.user?.displayName || undefined;
  const firstName = current.user?.firstName || undefined;
  const lastName = current.user?.lastName || undefined;
  const email = current.user?.email || undefined;
  const tier =
    current.user?.rosterOrganization?.sponsorship?.currentTier
      ?.analyticsTierType;
  const role = current.user?.analyticsRoleType;
  const integrationIsMember = !!current.user?.integrationIsMember;
  const platformIsMember =
    current.user?.membershipData?.membershipStatus === MembershipStatus.MEMBER;
  const isMember =
    integrationIsMember ||
    platformIsMember ||
    current.user?.analyticsUserType === 'member';
  const membershipStatus = isMember
    ? MEMBERSHIP_STATUS.MEMBER
    : MEMBERSHIP_STATUS.NONMEMBER;

  return {
    properties: {
      currentUserId,
      associationCode,
      vendorDirectory,
      communityAccess,
      userType,
      displayName,
      firstName,
      lastName,
      email,
      tier,
      role,
      membershipStatus,
      integrationId: current.user?.integrationId ?? undefined,
      integrationIsMember,
      platformIsMember,
      isMember,
    },
  };
};
